import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  sendFromProgram,
  TProgramSendKind,
  TProgramSendResult,
} from '../../../requests/opsProgramSends';
import { TOpsProgramDetail } from '../../../types';
import { btnSecondary, Card, inputClass, labelClass } from '../ui';

type Props = {
  programId: string;
  program: TOpsProgramDetail;
};

function outcome(r: TProgramSendResult): string {
  const bits: string[] = [];
  if (r.kind === 'welcome') {
    bits.push(
      `${r.bccCount} member${r.bccCount === 1 ? '' : 's'} in Bcc, ${
        r.cc?.length ? `POC copied` : 'no POC email to copy'
      }`,
    );
    if (r.membersWithoutEmail) {
      bits.push(`${r.membersWithoutEmail} without an email, left out`);
    }
  } else {
    bits.push(`to ${r.to}`);
  }
  bits.push(
    r.status === 'sent'
      ? 'sent'
      : r.status === 'held'
      ? `queued (held: ${r.holdReason ?? 'review'})`
      : r.status === 'suppressed'
      ? `held — ${r.holdReason ?? 'quiet account'}`
      : r.status,
  );
  if (r.warning) bits.push(r.warning);
  return bits.join(' · ');
}

/**
 * Send from this program — the Team Pass welcome to the members and the
 * CSM handoff to the POC, both through the comms engine.
 */
const SendCard: React.FC<Props> = ({ programId, program }) => {
  const queryClient = useQueryClient();
  const [handoffTo, setHandoffTo] = useState('');
  const send = useMutation({
    mutationFn: (body: { kind: TProgramSendKind; to?: string }) =>
      sendFromProgram(programId, body),
    onSuccess: (r) => {
      showToast(
        `${
          r.kind === 'welcome' ? 'Team Pass welcome' : 'CSM handoff'
        }: ${outcome(r)}`,
        'success',
      );
      queryClient.invalidateQueries({ queryKey: ['opsProgram', programId] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const members = program.members ?? [];
  const reachable = members.filter((m) => m.enabled && m.email).length;
  const busy = send.isPending;

  return (
    <Card title="Send from this program">
      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label className={labelClass}>Team Pass welcome to the members</label>
          <div className="text-sm text-gray-five">
            {reachable} of {members.length} member
            {members.length === 1 ? '' : 's'} enabled with an email — they go in
            Bcc, the contract POC in Cc, from and to cx@.
          </div>
          <button
            type="button"
            className={btnSecondary}
            disabled={busy || reachable === 0}
            onClick={() => send.mutate({ kind: 'welcome' })}
          >
            Send Team Pass welcome
          </button>
          <div className="text-xs text-gray-bold">
            "Welcome to your Learnit Team Pass", rendered against this contract
            with its program link. One message; members never see each other.
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className={labelClass}>CSM handoff to the POC</label>
          <input
            className={inputClass}
            placeholder="To the contract POC, or type another address"
            value={handoffTo}
            onChange={(e) => setHandoffTo(e.target.value)}
            disabled={busy}
          />
          <button
            type="button"
            className={btnSecondary}
            disabled={busy}
            onClick={() =>
              send.mutate({
                kind: 'handoff',
                ...(handoffTo.trim() ? { to: handoffTo.trim() } : {}),
              })
            }
          >
            Send CSM handoff
          </button>
          <div className="text-xs text-gray-bold">
            "Program Hand Off CSM - TEAM PASS" — the kickoff invitation, copy to
            cx@.
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SendCard;
